// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "@reach/router";
import { Button, Divider, Form, message, Modal, Popconfirm, Spin } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  SendOutlined
} from "@ant-design/icons";
import {
  formItemBlockLayout,
  LINKS_NUMBER_BY_NEWS_THEME
} from "../../../../utils/Constant";
import {
  DeleteOutlined,
  SaveOutlined,
  StopOutlined
} from "@ant-design/icons/lib";
import { addNewsTheme } from "../../../../state/newsTheme/NewsThemeEvents";
import NewsThemeInputFormSection from "../details/NewsThemeInputFormSection";
import NewsThemeColorFormSection from "../details/NewsThemeColorFormSection";
import {
  requestDeleteNewsTheme,
  requestGetNewsTheme,
  requestUnpublishNewsTheme,
  requestUpdateNewsTheme
} from "../../../../state/newsTheme/NewsThemeEffects";
import NewsThemeContactFormSection from "./NewsThemeContactFormSection";
import NewsThemeLinksFormSection from "./NewsThemeLinksFormSection";
import {
  NewsThemeEditFormProps,
  NewsThemeLinkDTO
} from "../../../types/NewsThemeTypes";
import { Field } from "../../../types/NewsTypes";
import {
  NewsThemeInfoResponseDTO,
  NewsThemeWriteRequestDTO
} from "../../../../api/backend-openapi";
import { showConfirm } from "../../../components/ConfirmModal";
import i18n from "i18next";
import NewsThemeStatusTag from "./NewsThemeStatusTag";
import { validate } from "../../../../validator/NewsThemeValidator";
import { ValidatorResult } from "../../../types/Types";
import { displayError } from "../../../../utils/displayError";

const { confirm } = Modal;

const NewsThemeForm: React.FunctionComponent<NewsThemeEditFormProps> = (
  props: NewsThemeEditFormProps
) => {
  const {
    newsThemeId,
    editMode,
    setEditMode,
    canEdit,
    canDelete,
    canValidate
  } = props;
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const [newsTheme, setNewsTheme] = useState<NewsThemeInfoResponseDTO | null>(
    null
  );

  const [color, setColor] = useState<string>();

  const [fields, setFields] = useState<Field[]>([]);

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const [getResponseReceived, setGetResponseReceived] = useState<boolean>(
    false
  );

  const confirmationPopup = async (publish?: boolean) => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("global.confirm"),
        content: publish
          ? t("news_theme.publish")
          : publish !== undefined
          ? t("news_themes.table.confirmation.unpublish")
          : t("news_theme.save"),
        okText: t("global.yes"),
        cancelText: t("global.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  useEffect(() => {
    if (newsThemeId) {
      requestGetNewsTheme(newsThemeId);
    }
  }, [newsThemeId]);

  useEffect(() => {
    return requestGetNewsTheme.done.watch(({ result }) => {
      setGetResponseReceived(true);
      if (result.ok && result.data) {
        setNewsTheme(result.data);
        setColor(result.data.color);
        const links: Field[] = [];
        result.data.links &&
          result.data.links.forEach((newsThemeLink, index) => {
            links.push(
              ...[
                {
                  name: [`link${index}Title`],
                  value: newsThemeLink.title
                },
                {
                  name: [`link${index}Url`],
                  value: newsThemeLink.url
                },
                {
                  name: [`link${index}Detail`],
                  value: newsThemeLink.detail
                }
              ]
            );
          });
        setFields([
          {
            name: ["name"],
            value: result.data.name
          },
          {
            name: ["contactMail"],
            value: result.data.contactMail
          },
          {
            name: ["contactLabel"],
            value: result.data.contactLabel
          },
          ...links,
          {
            name: ["branch"],
            value: result.data.branch
          }
        ]);
      } else {
        displayError("news_themes.not_found");
      }
    });
  });

  useEffect(() => {
    return requestUpdateNewsTheme.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        addNewsTheme(result.data);
        message.success(t("edit_news_theme.success"));
        navigate("/newsThemes");
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestUnpublishNewsTheme.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        addNewsTheme(result.data);
        message.success(t("edit_news_theme.success"));
        navigate("/newsThemes");
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestDeleteNewsTheme.done.watch(({ result }) => {
      if (result.ok) {
        message.success(t("news_themes.global_buttons.delete.success"));
        navigate("/newsThemes");
      } else {
        displayError("global.on_finish_failed");
      }
    });
  });

  const handleSubmit = (
    values: { [key: string]: string | undefined },
    publish?: boolean
  ) => {
    const validationResult: ValidatorResult = validate({ ...values, color });
    if (!validationResult.validate) {
      displayError("global.on_finish_failed");
    } else {
      confirmationPopup(publish).then((confirmed: boolean) => {
        if (confirmed) {
          setButtonLoading(true);
          const links: NewsThemeLinkDTO[] = [];
          for (let i = 0; i < LINKS_NUMBER_BY_NEWS_THEME; i++) {
            links.push({
              title: values[`link${i}Title`] || "",
              detail: values[`link${i}Detail`] || "",
              url: values[`link${i}Url`] || ""
            });
          }
          if (publish === false) {
            const newsThemeToUpdate: { published: boolean } = {
              published: false
            };

            requestUnpublishNewsTheme({
              id: newsThemeId,
              dto: newsThemeToUpdate
            });
          } else {
            const newsThemeToUpdate: NewsThemeWriteRequestDTO = {
              name: values.name || "",
              published: publish || false,
              contactMail: values.contactMail,
              contactLabel: values.contactLabel,
              color,
              links,
              branch: values.branch || ""
            };
            requestUpdateNewsTheme({
              id: newsThemeId,
              dto: newsThemeToUpdate
            });
          }

          setButtonLoading(true);
        }
      });
    }
  };

  const handleSave = (values: any) => {
    handleSubmit(values);
  };

  const handlePublish = () => {
    form
      .validateFields()
      .then(values => {
        handleSubmit(values, true);
      })
      .catch(() => {
        displayError("global.on_finish_failed");
      });
  };
  const handleUnpublish = () => {
    form
      .validateFields()
      .then(values => {
        handleSubmit(values, false);
      })
      .catch(() => {
        displayError("global.on_finish_failed");
      });
  };

  const spinIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const onFinishFailed = ({ errorFields }: any) => {
    displayError("global.on_finish_failed");
    form.scrollToField(errorFields[0].name);
  };

  return (
    <>
      {!getResponseReceived ? (
        <div style={{ textAlign: "center" }}>
          <Spin indicator={spinIcon} />
        </div>
      ) : newsTheme ? (
        <Form
          {...formItemBlockLayout}
          form={form}
          onFinish={handleSave}
          onFinishFailed={onFinishFailed}
          fields={fields}
        >
          <div className="form-first-item-sticky-top-container">
            <div className="form-first-item-sticky-top-content">
              <div className="d-flex align-items-center justify-content-between flex-wrap px-3">
                <div className="my-3">
                  <NewsThemeStatusTag
                    publicationDate={newsTheme.publicationDate}
                  />
                </div>
                <Divider orientation="left">
                  <h3 className="text-secondary mb-0">
                    {t("news.news_actions")}
                  </h3>
                </Divider>
                <div className="my-3">
                  {editMode ? (
                    <>
                      <Popconfirm
                        title={t("global.cancel_confirmation")}
                        okText={t("global.yes")}
                        cancelText={t("global.no")}
                        onConfirm={() => {
                          setEditMode(false);
                        }}
                        placement="top"
                        icon={<QuestionCircleOutlined />}
                        className="m-2"
                      >
                        <Button htmlType="reset">
                          <StopOutlined /> {t("global.cancel")}
                        </Button>
                      </Popconfirm>
                    </>
                  ) : (
                    <>
                      <div className="d-flex align-items-center justify-content-center flex-wrap">
                        <Button
                          htmlType="reset"
                          className="m-2"
                          onClick={() => navigate("/newsThemes")}
                        >
                          <ArrowLeftOutlined /> {t("edit_news_theme.return")}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
                <div className="my-3">
                  {editMode ? (
                    <>
                      {newsTheme.publicationDate === null && (
                        <Button
                          className="btn-primary m-2"
                          htmlType="submit"
                          loading={buttonLoading}
                        >
                          <SaveOutlined /> {t("global.save")}
                        </Button>
                      )}
                      {canValidate && (
                        <Button
                          className="btn-success m-2"
                          onClick={handlePublish}
                          loading={buttonLoading}
                        >
                          <SendOutlined /> {t("global.publish")}
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="d-flex align-items-center justify-content-center flex-wrap">
                        {canValidate && newsTheme.publicationDate === null && (
                          <Button
                            className="btn-success m-2"
                            onClick={handlePublish}
                            loading={buttonLoading}
                          >
                            <SendOutlined /> {t("global.publish")}
                          </Button>
                        )}

                        {canValidate &&
                          newsTheme.publicationDate !== null &&
                          !newsTheme.isDefault && (
                            <Button
                              className="btn-secondary mr-1 btn-list"
                              onClick={handleUnpublish}
                              loading={buttonLoading}
                            >
                              <StopOutlined /> {t("news_theme.unpublish")}
                            </Button>
                          )}
                        {canEdit &&
                          ((canValidate &&
                            newsTheme.publicationDate !== null) ||
                            newsTheme.publicationDate === null) && (
                            <Button
                              htmlType="reset"
                              className="btn-primary m-2"
                              onClick={() => setEditMode(true)}
                            >
                              <EditOutlined /> {t("edit_news_theme.button")}
                            </Button>
                          )}
                        {canDelete &&
                          !newsTheme.isDefault &&
                          newsTheme.publicationDate === null && (
                            <Button
                              className="btn-danger m-2"
                              onClick={() =>
                                showConfirm(
                                  i18n.t(
                                    "news_themes.table.confirmation.delete",
                                    {
                                      news: newsTheme.name
                                    }
                                  ),
                                  () => {
                                    requestDeleteNewsTheme(newsTheme);
                                  }
                                )
                              }
                            >
                              <DeleteOutlined /> {t("edit_news_theme.delete")}
                            </Button>
                          )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">{t("news_theme.infos")}</h3>
          </Divider>
          <NewsThemeInputFormSection
            newsTheme={newsTheme}
            editMode={editMode}
          />
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">{t("news_theme.color")}</h3>
          </Divider>
          <NewsThemeColorFormSection
            color={color}
            setColor={setColor}
            editMode={editMode}
          />
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">
              {t("news_theme.contact.title")}
            </h3>
          </Divider>
          <NewsThemeContactFormSection editMode={editMode} />
          <NewsThemeLinksFormSection editMode={editMode} />
        </Form>
      ) : (
        <div>{t("news_theme.not_found")}</div>
      )}
    </>
  );
};
export default NewsThemeForm;
